import { HttpHeaders, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Callback, CognitoUtil } from './cognito.service';
import * as AWS from 'aws-sdk';
import { TenantInfo } from '../tenant/tenant-info';
import { AppConfig } from '../app.config';
import { stringify } from 'querystring';
import { getResponseFromJSONData } from '../common/json-data-service';


// declare var AMA: any;

export class WebAPIConfig {
    apiPath: string;
    isGet?: boolean;
    isPost?: boolean;
    isPut?: boolean;
    headerBuilder?: (token: string) => HttpHeaders;
    retryConfig?: {
        retryAttempts: number;
    };
}

@Injectable()
export class AwsUtil {
    public static firstLogin = false;
    public static runningInit = false;

    private tenantInfo: TenantInfo;

    constructor(public cognitoUtil: CognitoUtil,
        private appConfig: AppConfig,
        protected http: HttpClient) {
        this.tenantInfo = appConfig.getTenantInfo();
        AWS.config.region = this.tenantInfo.region;
    }

    /**
     * This is the method that needs to be called in order to init the aws global creds
     */
    initAwsService(callback: Callback, isLoggedIn: boolean, idToken: string) {
        if (AwsUtil.runningInit) {
            // Need to make sure I don't get into an infinite loop here, so need to exit if this method is running already
            console.log('AwsUtil: Aborting running initAwsService()...it is running already.');
            // instead of aborting here, it's best to put a timer
            if (callback != null) {
                callback.callback();
                callback.callbackWithParam(null);
            }
            return;
        }


        console.log('AwsUtil: Running initAwsService()');
        AwsUtil.runningInit = true;


        const mythis = this;
        // First check if the user is authenticated already
        if (isLoggedIn) {
            mythis.setupAWS(isLoggedIn, callback, idToken);
        }
    }


    /**
     * Sets up the AWS global params
     *
     * @param isLoggedIn
     * @param callback
     */
    setupAWS(isLoggedIn: boolean, callback: Callback, idToken: string): void {
        console.log('AwsUtil: in setupAWS()');
        if (isLoggedIn) {
            console.log('AwsUtil: User is logged in');
            // Setup mobile analytics
            // var options = {
            //     appId: '32673c035a0b40e99d6e1f327be0cb60',
            //     appTitle: 'aws-cognito-angular2-quickstart'
            // };

            // TODO: The mobile Analytics client needs some work to handle Typescript. Disabling for the time being.
            // var mobileAnalyticsClient = new AMA.Manager(options);
            // mobileAnalyticsClient.submitEvents();

            // Comment this out to see if we can get rid of that error we see on initial login...
            // this.addCognitoCredentials(idToken);

            console.log('AwsUtil: Retrieving the id token');

        } else {
            console.log('AwsUtil: User is not logged in');
        }

        if (callback != null) {
            callback.callback();
            callback.callbackWithParam(null);
        }

        AwsUtil.runningInit = false;
    }

    addCognitoCredentials(idTokenJwt: string): void {
        const creds = this.cognitoUtil.buildCognitoCreds(idTokenJwt);

        AWS.config.credentials = creds;

        creds.get(function (err) {
            if (!err) {
                if (AwsUtil.firstLogin) {
                    // save the login info to DDB
                    this.ddb.writeLogEntry('login');
                    AwsUtil.firstLogin = false;
                }
            }
        });
    }

    // static getCognitoParametersForIdConsolidation(idTokenJwt: string): {} {
    //     console.log('AwsUtil: enter getCognitoParametersForIdConsolidation()');
    //     let url = 'cognito-idp.' + CognitoUtil._REGION.toLowerCase() + '.amazonaws.com/' + CognitoUtil._USER_POOL_ID;
    //     let logins: Array<string> = [];
    //     logins[url] = idTokenJwt;
    //     let params = {
    //         IdentityPoolId: CognitoUtil._IDENTITY_POOL_ID, /* required */
    //         Logins: logins
    //     };

    //     return params;
    // }

    lambda(invokeParam: AWS.Lambda.Types.InvocationRequest): Promise<AWS.Lambda.Types.InvocationResponse> {
        const lambda = new AWS.Lambda();
        return this.cognitoUtil.getAccessToken()
            .then(() => {
                return lambda.invoke(invokeParam).promise();
            });
    }

    webAPIRequestHeaders(token: any): HttpHeaders {
        const headers = new HttpHeaders()
            .set('Content-Type', 'application/json')
            .set('Authorization', String(token));
        return headers;
    }

    async webAPICall<T>(apiConfig: WebAPIConfig, params: any): Promise<T>;
    async webAPICall<T>(apiPath: string, params: any, isPost?: boolean, headerBuilder?: (token: string) => HttpHeaders): Promise<T>;
    async webAPICall<T>(configOrPath: WebAPIConfig | string,
        params: any,
        isPost?: boolean,
        headerBuilder?: (token: string) => HttpHeaders): Promise<T> {
        let result: T;
        let apiPath;
        let isGet = true;
        let isPut = false;
        let webApiConfig: WebAPIConfig;
        if (typeof configOrPath === 'string') {
            apiPath = configOrPath;
            isGet = !isPut;
        } else {
            apiPath = configOrPath.apiPath;
            isGet = configOrPath.isGet;
            isPost = configOrPath.isPost;
            isPut = configOrPath.isPut;
            headerBuilder = configOrPath.headerBuilder;
            webApiConfig = configOrPath;
        }
        try {
            const authToken: any = await this.cognitoUtil.getAccessToken();
            let headers = headerBuilder ? headerBuilder(authToken) : this.webAPIRequestHeaders(authToken);
            if (!isPost) {
                // Only use compression for the GETs so we dont have to deal with pre-compression on the 'save' type of calls.
                headers = headers.set('Content-Encoding', 'gzip');
            }
            const options = { headers: headers };
            if (headers.get('Content-Type') !== 'application/json') {
                options['responseType'] = 'text';
            }
            const url = this.tenantInfo.apiUrl + apiPath;
            if (isPost) {
                result = await this.http.post<T>(url, params, options).toPromise();
            } else if (isPut) {
                result = await this.http.put<T>(url, params, options).toPromise();
            } else {
                const queryString = stringify(params);
                if (this.appConfig.getUseJSONData()) {
                    return getResponseFromJSONData(this.http, url, queryString);
                } 
                result = await this.http.get<T>(url + '?' + queryString, options).toPromise();
            }
            // .catch((error: HttpErrorResponse) => {
            //     const JsonParseError = 'Http failure during parsing for';
            //     const matches = error.message.match(new RegExp(JsonParseError, 'ig'));
            //     if (error.status === 200 && matches.length === 1) {
            //         return Promise.resolve(error.error.text);
            //     } else {
            //         return Promise.reject(error);		// re-throw
            //     }
            // })
            ;
        } catch (err) {
            if (webApiConfig && webApiConfig.retryConfig && webApiConfig.retryConfig.retryAttempts > 0) {
                webApiConfig.retryConfig.retryAttempts -= 1;
                console.log(`API call for ${apiPath} failed but retrying. Remaining attempts: ${webApiConfig.retryConfig.retryAttempts}`);
                result = await this.webAPICall(webApiConfig, params);
            } else {
                throw err;
            }
        }
        return result;
    }
}
